/**
 * Повернення до екрана на те саме місце (UX-08; links-tabs.md): прокрутка й рядок-якір (запис,
 * з яким людина працювала). Фільтри й подання живуть в адресі, дані — у кеші запитів, тож тут —
 * лише те, чого в них немає.
 *
 * - Ключ — екран (`list:<подання>`), а не запис історії: повернення «Назад», крихтою, `Esc` чи
 *   пунктом навігації відкриває список на тому самому місці.
 * - Стан живе в пам'яті й у `sessionStorage` вкладки (переживає `F5`): ≤ 20 екранів, 30 хв
 *   (NN/g: зберігати позицію в межах сесії, а після годин відсутності — починати спочатку).
 * - Фокус на якір ставимо лише при поверненні: «Назад» / «Вперед» браузера, `F5` або перехід з
 *   позначкою повернення (`returnState`). Звичайний перехід фокус не забирає.
 * - Лише ідентифікатори записів і числа — без назв і значень полів.
 */
import type { RouterHistory } from '@tanstack/react-router';

export interface ScreenMemory {
  /** Прокрутка контейнера екрана, px. */
  scroll: number;
  /** Запис-якір: відкритий з цього екрана або той, на якому людина зупинилась `J` / `K`. */
  anchor?: string;
  /** Номер рядка якоря: якщо якоря вже немає, фокус — на сусідньому рядку. */
  index?: number;
  /**
   * Прокрутка частин екрана за ключем частини, px: стовпці дошки (`<воронка>:<етап>`), панелі
   * вкладок картки (`<запис>:<вкладка>`). Не більше `MAX_PARTS`, найстаріші забуваються.
   */
  parts?: Record<string, number>;
  at: number;
}

const KEY = 'cl:return';
const MAX_SCREENS = 20;
/** Частин на екран: дошка — до 20 стовпців кожної воронки, картки — кілька вкладок записів. */
export const MAX_PARTS = 40;
export const RETURN_TTL_MS = 30 * 60_000;

let memory: Record<string, ScreenMemory> | null = null;

function load(): Record<string, ScreenMemory> {
  if (memory) return memory;
  memory = {};
  try {
    const raw = JSON.parse(sessionStorage.getItem(KEY) ?? '{}') as Record<string, ScreenMemory>;
    for (const [key, value] of Object.entries(raw)) {
      if (typeof value?.scroll === 'number' && typeof value.at === 'number') memory[key] = value;
    }
  } catch {
    // Сховище недоступне чи зіпсоване — починаємо з порожнього.
  }
  return memory;
}

function persist(): void {
  if (!memory) return;
  try {
    sessionStorage.setItem(KEY, JSON.stringify(memory));
  } catch {
    // Приватний режим: позиція переживе перехід, але не `F5`.
  }
}

/** Пам'ять екрана, якщо вона свіжа. */
export function readScreen(key: string, now = Date.now()): ScreenMemory | undefined {
  const value = load()[key];
  return value && now - value.at < RETURN_TTL_MS ? value : undefined;
}

/** Оновити пам'ять екрана (частково) і записати у вкладку. */
export function saveScreen(key: string, patch: Partial<ScreenMemory>, now = Date.now()): void {
  const all = load();
  const prev = all[key] && now - all[key].at < RETURN_TTL_MS ? all[key] : { scroll: 0, at: now };
  all[key] = { ...prev, ...patch, at: now };
  const keys = Object.keys(all).sort((a, b) => (all[b]?.at ?? 0) - (all[a]?.at ?? 0));
  for (const stale of keys.slice(MAX_SCREENS)) delete all[stale];
  persist();
}

/** Прокрутка частини екрана (стовпця, панелі вкладки), якщо пам'ять екрана свіжа. */
export function readPart(key: string, part: string, now = Date.now()): number | undefined {
  return readScreen(key, now)?.parts?.[part];
}

/** Записати прокрутку частини: остання записана — найновіша, понад `MAX_PARTS` — геть. */
export function savePart(key: string, part: string, scroll: number, now = Date.now()): void {
  const { [part]: _old, ...rest } = readScreen(key, now)?.parts ?? {};
  const entries = Object.entries(rest).slice(-(MAX_PARTS - 1));
  saveScreen(key, { parts: { ...Object.fromEntries(entries), [part]: scroll } }, now);
}

/** Ключ пам'яті списку подання. */
export const listScreenKey = (view: string) => `list:${view}`;
/** Дошка подання (KAN-12): прокрутка стрічки стовпців і картка-якір. */
export const boardScreenKey = (view: string) => `board:${view}`;
/** Картки однієї сутності: прокрутка панелей вкладок (частина — `<запис>:<вкладка>`). */
export const cardScreenKey = (entity: string) => `card:${entity}`;

// ---------------------------------------------------------------- чи це повернення

type Arrival = 'push' | 'back' | 'reload';
let arrival: Arrival = initialArrival();

function initialArrival(): Arrival {
  try {
    const entry = performance.getEntriesByType?.('navigation')[0] as
      | PerformanceNavigationTiming
      | undefined;
    return entry?.type === 'reload' || entry?.type === 'back_forward' ? 'reload' : 'push';
  } catch {
    return 'push';
  }
}

/**
 * Слухач історії роутера: «Назад» / «Вперед» — повернення. Підключає `createAppRouter`.
 * Перехід з `state.returnState` (крихта, `Esc` без запису історії позаду) — теж повернення.
 */
export function trackArrivals(history: RouterHistory): () => void {
  return history.subscribe(({ action, location }) => {
    const flagged = (location.state as { returnState?: boolean } | undefined)?.returnState;
    // REPLACE (перехід `J` / `K` у картці, переадресація) не змінює, як прийшли на екран.
    if (action.type === 'REPLACE' && !flagged) return;
    arrival = action.type === 'BACK' || action.type === 'FORWARD' || flagged ? 'back' : 'push';
  });
}

/** Екран відкрито поверненням (а не новим переходом): відновлювати й фокус. */
export const isReturn = () => arrival !== 'push';

/** Лише для тестів. */
export function resetReturnStateForTests(next: Arrival = 'push'): void {
  memory = null;
  arrival = next;
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // jsdom без сховища.
  }
}
